/**
 * 分佣（返佣）相关接口 —— 用户端。
 *
 * 对应后端 `com.nwbd.controller.CommissionController`（`@RequestMapping("/commission")`）。
 *
 * <p>⚠️ 路径**不带** `/ai-video` 前缀：该 Controller 挂在根路径下，
 * 前端基址 `VITE_APP_BASE_URL = "/AiVideo"` 会被 vite 代理剥掉，
 * 所以 `"/commission/summary"` → 后端 `/commission/summary`。</p>
 *
 * <p>原型：`docs/prototype/会员分佣-原型-真实版-20261012.html` 的 `screen-commission`（我的分佣）。
 * 口径见 `docs/返佣功能-实现方案-终稿-20261010.md` §3.5 / §4.1。</p>
 */

import { httpGet, httpPost, httpPut, ServerType } from "@/service/httpRequest";

/** 「我的分佣」概览（对应 `GET /commission/summary`） */
export interface CommissionSummary {
    /** 我的邀请码（存量账号首次调用会懒生成，故恒有值） */
    inviteCode: string | null;
    /**
     * 扫码 / 点击次数（推荐官漏斗第一环）。
     *
     * <p>来源：{@code nw_invite_visit} 里归属我的落地流水条数。
     * 落地页由 {@link reportInviteScan} 上报；**同一浏览器会话内对同一个码只上报一次**，
     * 所以刷新 / 前进后退不会灌水。</p>
     *
     * <p>⚠️ 页面已不展示（产品要求下线），字段与上报链路保留 —— 恢复展示时直接用即可。</p>
     */
    scanCount: number;
    /**
     * 绑定粉丝数（旧口径，**只含 L1 直推**）。
     *
     * <p>页面已改用 {@link boundFansL1} / {@link boundFansL2} 分开展示，
     * 本字段保留仅为兼容（值恒等于 `boundFansL1`）。</p>
     */
    boundFans: number;
    /** L1 关联用户：直接绑定我的人（= `nw_referral_binding.inviter_id = 我`） */
    boundFansL1: number;
    /** L2 关联用户：我的 L1 粉丝名下的人（= `nw_referral_binding.grand_inviter_id = 我`） */
    boundFansL2: number;
    /** 累计成交：我名下产生过佣金的**去重订单数** */
    dealCount: number;
    /** 待结算佣金合计（元） */
    pendingAmount: number;
    /**
     * 待结算里**订单仍未付款**的部分（元）。
     *
     * <p>2026-10-20 起计佣前移到「下单即待结算」，所以 `pendingAmount` 会包含未付款订单的
     * 预估佣金；页面在待结算下方挂小字「含未付款订单 X 豆」用这个字段。</p>
     */
    pendingUnpaidAmount: number;
    /** 已结算佣金净额（元）：已扣除退款「负数冲抵」的部分 */
    settledAmount: number;
    /** 待结算明细条数 */
    pendingCount: number;
    /** 已结算明细条数 */
    settledCount: number;
    /** 直推（L1）比例，如 0.05 */
    rateL1: number | null;
    /** 二代（L2）比例，如 0.025 */
    rateL2: number | null;
    /** 结算形式：当前恒为 `"BEAN"`（返佣豆，非现金、不支持提现） */
    settleMode: string;
    /** 元 → 梦想豆换算比（1 元 = N 颗），当前为 5 */
    beanPerYuan: number;
    /** 覆盖的商品类型：`MEMBER` / `RECHARGE` */
    coverBizTypes: string[];
}

/** 佣金明细行（对应 `CommissionRecordVO`） */
export interface CommissionRecordItem {
    /** 雪花 ID，后端以字符串下发 */
    id: string;
    /** 计佣时间，格式 `yyyy-MM-dd HH:mm:ss` */
    createTime: string;
    /** 来源粉丝（买家）ID */
    sourceUserId: string;
    /** 来源粉丝昵称（可能为空，前端兜底为 `#id`） */
    sourceNickname: string | null;
    /** 层级：1 直推 / 2 二代 */
    level: number;
    /** 来源订单号 */
    orderNo: string;
    /** 订单内容，如「50 条档会员」/「影豆充值-进阶档」 */
    orderBody: string | null;
    /** 商品类型：`MEMBER` / `RECHARGE` */
    bizType: string;
    /** 计佣基数（订单实付金额） */
    baseAmount: number;
    /** 本层比例，如 0.05 */
    rate: number;
    /** 本层佣金 */
    commissionAmount: number;
    /** 1 待结算 / 2 已结算 / 3 已撤销 */
    status: number;
    /** 被退款冲抵的金额（正数；>0 说明这条已结算佣金已在退款时记负数冲抵，下期结算抵扣） */
    reversedAmount: number | null;
    /** 结算时间 */
    settledAt: string | null;
}

/** 佣金明细分页响应（与后端 `{ total, list }` 对齐） */
export interface CommissionRecordPage {
    total: number;
    list: CommissionRecordItem[];
}

/** 分页查询参数 */
export interface CommissionRecordQuery {
    page?: number;
    pageSize?: number;
}

/**
 * 「我的推荐」下级名单行（推荐官漏斗第二 / 三环）。
 *
 * <p>一行 = 一个**直推粉丝**（{@code nw_referral_binding.inviter_id = 我}）。
 * 口径与概览里的「绑定粉丝」严格一致（只含 L1，不含二代），
 * 所以名单总条数 == `summary.boundFansL1`。</p>
 */
export interface CommissionFanItem {
    /** 雪花 ID，后端以字符串下发 */
    userId: string;
    /** 昵称（可能为空，前端兜底为 `#id 后 6 位`） */
    nickname: string | null;
    /** 绑定时间（归属一次写入，永不更新） */
    bindTime: string | null;
    /** 绑定时使用的邀请码（审计用） */
    inviteCode: string | null;
    /** 绑定来源：`REGISTER` / `BIND` */
    bindSource: string | null;
    /** 该粉丝带来的成交单数（去重订单，已撤销不计） */
    dealCount: number;
    /** 该粉丝带来的计佣基数合计（已撤销不计） */
    dealAmount: number;
    /** 我因此拿到的 L1 佣金合计（已撤销不计） */
    commissionAmount: number;
}

/** 下级名单分页响应（与后端 `{ total, list }` 对齐） */
export interface CommissionFanPage {
    total: number;
    list: CommissionFanItem[];
}

/**
 * 查询「我的邀请码」。
 *
 * <p>与 `/summary` 里的 `inviteCode` 同源；单独保留一个轻量接口，便于只刷新邀请码卡片。</p>
 */
export const getMyInviteCode = (): Promise<{ inviteCode: string }> => {
    return httpGet<{ inviteCode: string }>(ServerType.Base, {
        url: "/commission/invite",
    });
};

/** 「我的分佣」概览：邀请码 + 绑定/成交计数 + 待结算/已结算 + 计佣口径 */
export const getCommissionSummary = (): Promise<CommissionSummary> => {
    return httpGet<CommissionSummary>(ServerType.Base, {
        url: "/commission/summary",
    });
};

/** 「我的分佣」佣金明细分页 */
export const getCommissionRecords = (params?: CommissionRecordQuery): Promise<CommissionRecordPage> => {
    return httpGet<CommissionRecordPage>(ServerType.Base, {
        url: "/commission/records",
        params: params || {},
    });
};

/** 「我的推荐」下级名单分页（直推粉丝 + TA 带来的成交与佣金） */
export const getCommissionFans = (params?: CommissionRecordQuery): Promise<CommissionFanPage> => {
    return httpGet<CommissionFanPage>(ServerType.Base, {
        url: "/commission/fans",
        params: params || {},
    });
};

/**
 * 邀请落地上报（**公开接口**，免登录免签名）。
 *
 * <p>带 `?invite=CODE` 打开站点时上报一次，用于填上「扫码 / 点击」计数。
 * 由 {@link ../utils/inviteCapture} 在捕获邀请码后 fire-and-forget 调用，
 * 同一浏览器会话内对同一个码只上报一次。</p>
 *
 * <p>⚠️ 后端对无效邀请码**静默忽略并返回成功**（不区分「码无效」与「码不存在」，
 * 否则这个公开接口就成了枚举有效邀请码的探针）。因此这里不需要、也不应该
 * 依据返回值做任何 UI 反馈。</p>
 */
export const reportInviteScan = (inviteCode: string): Promise<unknown> => {
    return httpPost<unknown>(ServerType.Base, {
        url: "/commission/scan",
        data: { inviteCode },
        // 旁路埋点：失败必须完全静默。拦截器会在 reject 之前弹 ElMessage，
        // 调用方的 .catch 拦不住 ⇒ 只有 silent 才能真正避免「服务器开小差了」红条。
        config: { silent: true } as any,
    });
};

// ============================================================================
// 管理端（AdminCommissionController，`/ai-video/admin/commission/*`，需管理员）
// ============================================================================

/** 管理端计佣规则行（对应 `NwCommissionRule`） */
export interface AdminCommissionRule {
    id: string;
    bizType: string;
    bizRefId: number | null;
    distType: number | null;
    rateL1: number;
    rateL2: number;
    maxLevel: number;
    /** null = 立即生效 */
    effectiveFrom: string | null;
    /** null = 长期有效 */
    effectiveTo: string | null;
    /** 1 启用 / 0 停用 */
    status: number;
    remark: string | null;
    createTime: string;
}

/** 管理端计佣明细行（对应 `AdminCommissionRecordVO`，比用户端多受益人与批次号） */
export interface AdminCommissionRecordItem {
    id: string;
    createTime: string;
    beneficiaryId: string;
    beneficiaryNickname: string | null;
    sourceUserId: string;
    sourceNickname: string | null;
    level: number;
    orderNo: string;
    orderBody: string | null;
    bizType: string;
    baseAmount: number;
    rate: number;
    commissionAmount: number;
    /** 1 待结算 / 2 已结算 / 3 已撤销 */
    status: number;
    /** 被退款冲抵的金额（正数；status 仍为 2，>0 说明已在退款时记负数冲抵，下期抵扣） */
    reversedAmount: number | null;
    settledAt: string | null;
    settleBatchNo: string | null;
}

export interface AdminPage<T> {
    total: number;
    list: T[];
}

export interface AdminCommissionRecordQuery {
    page?: number;
    pageSize?: number;
    bizType?: string;
    status?: number;
}

/** 管理端结算批次行（对应 `nw_commission_settlement` + 受益人昵称） */
export interface AdminCommissionSettlementItem {
    id: string;
    batchNo: string;
    period: string;
    userId: string;
    userNickname: string | null;
    /** 实发佣金净额 = 本期佣金 − 冲抵欠账 */
    totalAmount: number;
    /** 本期冲抵掉的欠账额（正数） */
    offsetAmount: number;
    /** 本期实际产生的佣金 = totalAmount + offsetAmount（后端派生） */
    grossAmount: number;
    beanAmount: number;
    recordCount: number;
    /** 1 已发放 */
    status: number;
    remark: string | null;
    createTime: string;
}

export interface AdminCommissionSettlementQuery {
    page?: number;
    pageSize?: number;
    period?: string;
}

/** 结算结果（对应 `CommissionSettlementService.settle` 返回） */
export interface CommissionSettleResult {
    /** 结算月份 YYYY-MM */
    period: string;
    /** 批次号，形如 CS202610 */
    batchNo: string;
    /** 本次实际结算的推荐官人数 */
    userCount: number;
    /** 因批次已存在而跳过的人数（幂等） */
    skipped: number;
    /** 本期**实发**佣金合计（净额） */
    totalAmount: number;
    /** 本期冲抵掉的欠账合计 */
    offsetAmount: number;
    /** 本期发放的返佣豆合计（颗） */
    beanAmount: number;
}

/** 计佣规则列表（管理端） */
export const getAdminCommissionRules = (): Promise<AdminCommissionRule[]> => {
    return httpGet<AdminCommissionRule[]>(ServerType.Base, {
        url: "/ai-video/admin/commission/rules",
    });
};

/**
 * 新增 / 编辑计佣规则的请求体（对应后端 `CommissionRuleSaveDto`）。
 *
 * <p>⚠️ 比例是「小数」而非百分数：5% 传 `0.05`。后端只接受 [0,1] 且最多 4 位小数。</p>
 */
export interface AdminCommissionRuleSave {
    /** 商品类型 MEMBER / RECHARGE（**仅新增必填**；编辑时后端忽略） */
    bizType?: string;
    /** 直推（L1）比例，0.05 = 5% */
    rateL1: number;
    /** 二代（L2）比例，0.025 = 2.5%（可为 0） */
    rateL2: number;
    /** 生效起 `yyyy-MM-dd HH:mm:ss`；null / 不传 = 立即生效 */
    effectiveFrom?: string | null;
    /** 生效止；null / 不传 = 长期有效 */
    effectiveTo?: string | null;
    /** 备注，最长 500 字 */
    remark?: string | null;
}

/**
 * 新增计佣规则（管理端）→ 返回新行 id。
 *
 * <p>同一商品类型只能有一条规则，重复新增会被后端以 SERVICE-00120 拒绝。</p>
 */
export const createAdminCommissionRule = (payload: AdminCommissionRuleSave): Promise<string> => {
    return httpPost<string>(ServerType.Base, {
        url: "/ai-video/admin/commission/rules",
        data: payload,
    });
};

/**
 * 编辑计佣规则（管理端）：比例 / 生效期 / 备注。
 *
 * <p>⚠️ **不会回溯历史明细** —— 已产生的佣金按当时的比例快照计，改价只影响之后的成交。</p>
 *
 * <p>`httpPut` 非泛型（返回 `Promise<unknown>`），故此处 async 包裹后返回 void。</p>
 */
export const updateAdminCommissionRule = async (id: string, payload: AdminCommissionRuleSave): Promise<void> => {
    await httpPut(ServerType.Base, {
        url: `/ai-video/admin/commission/rules/${id}`,
        data: payload,
    });
};

/**
 * 启用 / 停用计佣规则（管理端）。
 *
 * <p>⚠️ 停用后该类商品**不再计佣**（取不到启用规则时计佣静默跳过），调用方需二次确认。</p>
 */
export const updateAdminCommissionRuleStatus = (id: string, enabled: boolean): Promise<unknown> => {
    return httpPost<unknown>(ServerType.Base, {
        url: `/ai-video/admin/commission/rules/${id}/status`,
        params: { enabled },
    });
};

/** 全平台计佣明细分页（管理端） */
export const getAdminCommissionRecords = (params?: AdminCommissionRecordQuery): Promise<AdminPage<AdminCommissionRecordItem>> => {
    return httpGet<AdminPage<AdminCommissionRecordItem>>(ServerType.Base, {
        url: "/ai-video/admin/commission/records",
        params: params || {},
    });
};

/** 全平台结算明细分页（管理端） */
export const getAdminCommissionSettlements = (
    params?: AdminCommissionSettlementQuery,
): Promise<AdminPage<AdminCommissionSettlementItem>> => {
    return httpGet<AdminPage<AdminCommissionSettlementItem>>(ServerType.Base, {
        url: "/ai-video/admin/commission/settlements",
        params: params || {},
    });
};

/** 手动结算某月（补跑；幂等）。period 形如 `2026-09`，不传 = 上个月 */
export const settleCommission = (period?: string): Promise<CommissionSettleResult> => {
    return httpPost<CommissionSettleResult>(ServerType.Base, {
        url: "/ai-video/admin/commission/settle",
        params: period ? { period } : {},
    });
};

/**
 * 对账体检行（对应 `AdminCommissionReconcileVO`）：**一个推荐官的四组数字**。
 *
 * <p>钱包是**派生汇总表**，由「计佣 / 撤佣 / 冲抵 / 月结」四条写路径累加得出；
 * 任何一处漏扣或重复扣，界面只会**静默显示错钱**、不报错。本接口把钱包账面值与
 * 「明细表 + 结算批次表」反推的应有值并排摆出来。</p>
 *
 * <p>四条恒等式（来源账 = `nw_commission_record` + `nw_commission_settlement`）：</p>
 * <ul>
 *   <li>`pendingExpected` = Σ status=1 明细佣金</li>
 *   <li>`settledExpected` = Σ 批次实发净额 − Σ status=2 明细的 reversedAmount</li>
 *   <li>`offsetExpected` = Σ status=2 明细的 reversedAmount − Σ 批次已抵扣欠账</li>
 *   <li>`beanExpected` = Σ 批次 beanAmount</li>
 * </ul>
 *
 * <p>⚠️ 不要拿「明细 settled 求和」直接与钱包比：前者是**累计获得**、后者是**账面余额**，
 * 有欠账抵扣时本来就不同。这里的 `*Expected` 已经把冲抵与抵扣算进去了。</p>
 */
export interface AdminCommissionReconcileItem {
    /** 雪花 ID，后端以字符串下发 */
    userId: string;
    userNickname: string | null;
    /** 该推荐官的结算批次数（批次为 0 却有钱包余额 = 可疑） */
    batchCount: number;

    /** 钱包：待结算账面值 */
    pendingAmount: number;
    /** 应有：Σ 待结算明细佣金 */
    pendingExpected: number;
    /** 差额 = 钱包 − 应有（≠0 即有差异） */
    pendingDiff: number;

    /** 钱包：已结算账面值（账面余额） */
    settledAmount: number;
    /** 应有：Σ 批次实发净额 − Σ 已冲抵 */
    settledExpected: number;
    settledDiff: number;

    /** 钱包：待冲抵欠账账面值 */
    offsetAmount: number;
    /** 应有：Σ 已冲抵 − Σ 批次已抵扣欠账 */
    offsetExpected: number;
    offsetDiff: number;

    /** 钱包：已转返佣豆（颗） */
    beanConverted: number;
    /** 应有：Σ 批次 beanAmount（颗） */
    beanExpected: number;
    /** 差额（颗） */
    beanDiff: number;

    /**
     * 钱包：累计佣金账面值。
     *
     * <p>这一组是**内部自洽**而非外部对账：应有值 = 待结算 + 已结算，不依赖来源账，
     * 但能由同行的另外两个数字唯一确定 ⇒ 照样可查。对不上说明某条写路径漏维护该字段。</p>
     */
    totalAmount: number;
    totalExpected: number;
    totalDiff: number;

    /** 五组数字是否全部一致（后端计算） */
    balanced: boolean;
}

/** 对账体检总览（**全量**，不受 `onlyDiff` / `limit` 影响） */
export interface AdminCommissionReconcileSummary {
    accountCount: number;
    diffCount: number;
    /** 差额一律取绝对值再累加 */
    pendingDiffTotal: number;
    settledDiffTotal: number;
    offsetDiffTotal: number;
    beanDiffTotal: number;
    /** 累计口径差额绝对值合计（内部自洽） */
    totalDiffTotal: number;
}

/** 对账体检响应 */
export interface AdminCommissionReconcileResult {
    /** 本次是否只返回了有差异的账户 */
    onlyDiff: boolean;
    /** 明细行上限（服务端夹取后） */
    limit: number;
    accountCount: number;
    diffCount: number;
    /** 全部账平（`diffCount === 0`） */
    balanced: boolean;
    list: AdminCommissionReconcileItem[];
    summary: AdminCommissionReconcileSummary | null;
}

export interface AdminCommissionReconcileQuery {
    /** 只返回有差异的账户；不传默认 true */
    onlyDiff?: boolean;
    /** 明细行上限；不传默认 200（服务端夹到 1000） */
    limit?: number;
}

/**
 * 分佣对账体检（管理端，**只读**）。
 *
 * <p>只做比对与报告，**不修数** —— 差异涉及真实资金，是否修正需人工按单核对。</p>
 */
export const getAdminCommissionReconcile = (
    params?: AdminCommissionReconcileQuery,
): Promise<AdminCommissionReconcileResult> => {
    return httpGet<AdminCommissionReconcileResult>(ServerType.Base, {
        url: "/ai-video/admin/commission/reconcile",
        params: params || {},
    });
};
